import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAuth from '../../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import classnames from 'classnames';
import { useMemo } from 'react';
import Switch from '../inputs/Switch';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { COUPON_TYPES } from '../../services/exports/Constants';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';

interface Props {
  subtotal: number;
  className?: string;
}

export default function CashbackToggle(props: Props) {
  const { subtotal, className } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:CashbackToggle' });

  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { isLoggedIn } = useAuth();
  const { formatCurrency } = useAppMethods();
  const { hasCashbackProgram, cashbackProgram, balance, calculateSpendableCashback } = useCashbackProgram();

  const spendableCashback = useMemo(() => calculateSpendableCashback(subtotal), [subtotal, calculateSpendableCashback]);

  const onToggleClick = (checked: boolean) => {
    if (!checked) {
      return ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          coupon_type: null,
          coupon_details: null,
        }),
      );
    }

    return ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.CASHBACK,
        coupon_details: null,
      }),
    );
  };

  const renderAvailableBalance = () => {
    if (balance === 0) {
      return null;
    }

    if (balance > 0 && spendableCashback === 0) {
      return (
        <p className="text-sm">
          {t('feedback.min_spending_limit_not_reached', { value: cashbackProgram.min_spending_limit })}
        </p>
      );
    }

    return (
      <div className="flex justify-between">
        <div>
          <p className="text-sm">
            {t('balance', {
              value: spendableCashback,
              moneyValue: formatCurrency(spendableCashback / 100),
            })}
          </p>
          {!!cashbackProgram.max_spending_limit && (
            <p className="text-xs text-gray-700">
              {t('feedback.max_spending_limit', { value: cashbackProgram.max_spending_limit })}
            </p>
          )}
        </div>
        <Switch
          onStateText={t('states.on')}
          offStateText={t('states.off')}
          checked={cached_order?.coupon_type === COUPON_TYPES.CASHBACK}
          onChange={(e) => onToggleClick(e.target.checked)}
          className="my-auto"
        />
      </div>
    );
  };

  return (
    hasCashbackProgram &&
    isLoggedIn && <div className={classnames('w-full', className)}>{renderAvailableBalance()}</div>
  );
}
